<template lang="pug">
span.count-up(ref="rootRef" :aria-label="finalText") {{ displayText }}
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  value: number
  decimals?: number
  durationMs?: number
}>(), {
  decimals: 0,
  durationMs: 1500,
})

const rootRef = ref<HTMLElement>()
const { revealed, reducedMotion } = useRevealOnce(rootRef)
const shown = ref(0)
let frame: number | null = null

const formatter = computed(() => new Intl.NumberFormat('en-US', {
  minimumFractionDigits: props.decimals,
  maximumFractionDigits: props.decimals,
}))
const finalText = computed(() => formatter.value.format(props.value))
const displayText = computed(() => formatter.value.format(shown.value))

function easeOutQuint(t: number) {
  return 1 - (1 - t) ** 5
}

function run() {
  if (reducedMotion.value) {
    shown.value = props.value
    return
  }
  const start = performance.now()
  const tick = (now: number) => {
    const progress = Math.min(1, (now - start) / props.durationMs)
    shown.value = props.value * easeOutQuint(progress)
    if (progress < 1) {
      frame = requestAnimationFrame(tick)
    }
    else {
      shown.value = props.value
      frame = null
    }
  }
  frame = requestAnimationFrame(tick)
}

watch(revealed, (isRevealed) => {
  if (isRevealed) {
    run()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  if (frame !== null) {
    cancelAnimationFrame(frame)
  }
})
</script>

<style scoped>
.count-up {
  font-variant-numeric: tabular-nums;
}
</style>
